import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import { FiscalPeriod, FiscalPeriodStatus } from '../../services/types';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const statusOptions: FiscalPeriodStatus[] = ['Open', 'Closed', 'Locked'];

function fiscalYearDefaults() {
    const year = new Date().getFullYear();
    return {
        period_name: `FY ${year}`,
        start_date: `${year}-01-01`,
        end_date: `${year}-12-31`,
        status: 'Open' as FiscalPeriodStatus,
    };
}

export function FiscalPeriods() {
    const [periods, setPeriods] = useState<FiscalPeriod[]>([]);
    const [form, setForm] = useState(fiscalYearDefaults);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadPeriods = async () => {
        setLoading(true);
        setError(null);
        try {
            setPeriods(await api.financials.listFiscalPeriods());
        } catch (err: any) {
            const message = err?.message || 'Failed to load fiscal periods';
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPeriods();
    }, []);

    const createPeriod = async (event: FormEvent) => {
        event.preventDefault();
        setSaving(true);
        setError(null);
        try {
            await api.financials.createFiscalPeriod(form);
            toast.success('Fiscal period created');
            setForm(fiscalYearDefaults());
            await loadPeriods();
        } catch (err: any) {
            const message = err?.message || 'Failed to create fiscal period';
            setError(message);
            toast.error(message);
        } finally {
            setSaving(false);
        }
    };

    const updateStatus = async (period: FiscalPeriod, status: FiscalPeriodStatus) => {
        setSaving(true);
        setError(null);
        try {
            await api.financials.updateFiscalPeriodStatus(period.period_id, status);
            toast.success(`Period marked ${status}`);
            await loadPeriods();
        } catch (err: any) {
            const message = err?.message || 'Failed to update fiscal period';
            setError(message);
            toast.error(message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader>
                    <CardTitle>Fiscal Period Controls</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                        Create accounting periods and mark them Open, Closed, or Locked. Manual and automated journals are accepted only for Open periods.
                    </p>

                    <form onSubmit={createPeriod} className="grid gap-3 md:grid-cols-[1.2fr_1fr_1fr_0.8fr_auto]">
                        <label className="space-y-1 text-sm">
                            <span className="font-medium">Period name</span>
                            <input
                                value={form.period_name}
                                onChange={(event) => setForm({ ...form, period_name: event.target.value })}
                                required
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            />
                        </label>
                        <label className="space-y-1 text-sm">
                            <span className="font-medium">Start date</span>
                            <input
                                type="date"
                                value={form.start_date}
                                onChange={(event) => setForm({ ...form, start_date: event.target.value })}
                                required
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            />
                        </label>
                        <label className="space-y-1 text-sm">
                            <span className="font-medium">End date</span>
                            <input
                                type="date"
                                value={form.end_date}
                                onChange={(event) => setForm({ ...form, end_date: event.target.value })}
                                required
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            />
                        </label>
                        <label className="space-y-1 text-sm">
                            <span className="font-medium">Status</span>
                            <select
                                value={form.status}
                                onChange={(event) => setForm({ ...form, status: event.target.value as FiscalPeriodStatus })}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                                {statusOptions.map((status) => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </label>
                        <div className="flex items-end">
                            <Button type="submit" disabled={saving}>
                                Create Period
                            </Button>
                        </div>
                    </form>

                    {error && (
                        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                            {error}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Configured Periods</CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-sm text-muted-foreground">Loading periods...</p>
                    ) : periods.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No fiscal periods have been configured yet. Posting remains unrestricted until the first period is created.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b text-left text-muted-foreground">
                                        <th className="px-2 py-2 font-medium">Period</th>
                                        <th className="px-2 py-2 font-medium">Start</th>
                                        <th className="px-2 py-2 font-medium">End</th>
                                        <th className="px-2 py-2 font-medium">Status</th>
                                        <th className="px-2 py-2 text-right font-medium">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {periods.map((period) => (
                                        <tr key={period.period_id} className="border-b last:border-0">
                                            <td className="px-2 py-3 font-medium">{period.period_name}</td>
                                            <td className="px-2 py-3">{period.start_date}</td>
                                            <td className="px-2 py-3">{period.end_date}</td>
                                            <td className="px-2 py-3">{period.status}</td>
                                            <td className="px-2 py-3">
                                                <div className="flex justify-end gap-2">
                                                    {statusOptions.map((status) => (
                                                        <Button
                                                            key={status}
                                                            type="button"
                                                            size="sm"
                                                            variant={period.status === status ? 'default' : 'outline'}
                                                            disabled={saving || period.status === status}
                                                            onClick={() => updateStatus(period, status)}
                                                        >
                                                            {status}
                                                        </Button>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
